import { toast } from 'react-toastify'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { RootState } from 'app/store'
import RewardAbi from 'rewardAbi.json'
import StakingAbi from 'stakingAbi.json'
import TokenAbi from 'tokenAbi.json'
import Web3 from 'web3'
import { provider } from 'web3-core'
import { Contract } from 'web3-eth-contract'
import { AbiItem } from 'web3-utils'

import { fetchAccountData, switchNetwork, web3Modal } from './web3'
import { rewardAddress, stakingAddress, tokenAddress } from './web3.config'

export enum WalletStatus {
  connected = 'connected',
  disconnected = 'disconnected',
  loading = 'loading',
  rejected = 'rejected',
}

export interface Web3State {
  walletStatus: WalletStatus
  web3?: Web3
  provider?: provider
  staking?: Contract
  token?: Contract
  reward?: Contract
  selectedAccount?: string
  xoBalance?: number
}

const initialState: Web3State = {
  walletStatus: WalletStatus.disconnected,
}

export const connectWallet = createAsyncThunk(
  'web3/connectWallet',
  async () => {
    // eslint-disable-next-line @typescript-eslint/no-shadow

    web3Modal.clearCachedProvider()

    const provider = await web3Modal.connect()

    const web3 = new Web3(provider)

    await switchNetwork(web3)

    const staking = new web3.eth.Contract(
      StakingAbi as AbiItem[],
      stakingAddress
    )

    const token = new web3.eth.Contract(TokenAbi as AbiItem[], tokenAddress)

    const reward = new web3.eth.Contract(RewardAbi as AbiItem[], rewardAddress)

    const selectedAccount = await fetchAccountData(provider)

    return {
      web3,
      provider,
      staking,
      token,
      reward,
      selectedAccount,
    }
  }
)

export const disconnectWallet = createAsyncThunk(
  'web3/disconnectWallet',
  async (_, { getState }) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { provider } = (getState() as RootState).web3
    console.log('Killing the wallet connection', provider)
    // If the cached provider is not cleared,
    // WalletConnect will default to the existing session
    // and does not allow to re-scan the QR code with a new wallet.
    // Depending on your use case you may want or want not his behavir.
    web3Modal.clearCachedProvider()

    // if (provider !== undefined) {
    //   // TODO: Which providers have close method?
    //   if (provider?.close) {
    //     await provider.close()
    //   }
    // }
  }
)

export const getBalance = createAsyncThunk(
  'web3/getBalance',
  async (_, { getState }) => {
    const { selectedAccount, token, web3 } = (getState() as RootState).web3

    const result = await token?.methods.balanceOf(selectedAccount).call()

    const xoBalance = Number(web3?.utils.fromWei(result))
    console.log('balanceOf', xoBalance)
    return {
      xoBalance,
    }
  }
)

export const web3Slice = createSlice({
  name: 'web3',
  initialState,
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(connectWallet.pending, (_state) => {
        // TODO
      })
      .addCase(connectWallet.fulfilled, (state, action) => {
        const { selectedAccount } = action.payload

        console.log('selected', selectedAccount)

        Sentry.setUser({ username: selectedAccount })

        return {
          ...state,
          ...action.payload,
          walletStatus: WalletStatus.connected,
        }
      })
      .addCase(connectWallet.rejected, (state, action) => {
        toast.error('Wallet connection failed')
        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })
        state.walletStatus = WalletStatus.disconnected // TODO change to rejected
      })
      //   Disconnect
      .addCase(disconnectWallet.fulfilled, (state) => {
        // window.location.reload(false)
        state.provider = null
        state.walletStatus = WalletStatus.disconnected
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        state.xoBalance = action.payload.xoBalance
      })
  },
})

export default web3Slice.reducer
